import { useContext, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { allElements, FiltersPreferencesReportsEnum } from "constants/constants";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { UsersEntity } from "entities/Users/UsersEntity";
import { OeeDatePresetEnum, resolveOeeDatePreset } from "utilities/oeeDateRange";
import { filterObjectProperties } from "utilities/utilities";
import { initialState, OeeSummaryDashboardContext, validOeeSummaryDefaultFields } from "./OeeSummaryDashboardContext";
import {
    IOeeSummaryDashboardContextProvider,
    IOeeSummaryDashboardFilters,
} from "./OeeSummaryDashboardContextInterfaces";

export const OeeSummaryDashboardContextProvider: React.FC<IOeeSummaryDashboardContextProvider> = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [filters, setFilters] = useState<IOeeSummaryDashboardFilters>(initialState.filters);
    const [selectedProductionLineName, setSelectedProductionLineName] = useState<string>("");
    const [showAllProductionLines, setShowAllProductionLines] = useState<boolean>(true);
    const [showFilters, setShowFilters] = useState<boolean>(false);
    const [datePreset, setDatePreset] = useState<OeeDatePresetEnum>(initialState.datePreset);
    const [userFiltersDefault, setUserFiltersDefault] = useState<Partial<IOeeSummaryDashboardFilters> | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const dateRange = useMemo(() => resolveOeeDatePreset(datePreset), [datePreset]);

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault: Partial<IOeeSummaryDashboardFilters> = JSON.parse(
                userFilterPreference[0].filter_selection,
            );
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.MILL_DASHBOARD_OEE_SUMMARY,
        currentUser?.user_id,
    );

    const handleFiltersChange = (value: IOeeSummaryDashboardFilters) => {
        setFilters(value);
    };

    const handleSelectedProductionLineChange = (productionLineId: number | string, productionLineName: string) => {
        // Re-picking the line already in scope - the card clicked twice, or the fallback effect clearing the
        // scope again - must not touch the filters: it would wipe the machine centers and refetch for nothing.
        if (productionLineId === filters.productionLine) return;

        const allProductionLines = productionLineId === allElements;
        setShowAllProductionLines(allProductionLines);
        setSelectedProductionLineName(allProductionLines ? "" : productionLineName);
        setFilters((prev) => ({
            ...prev,
            // Machine center options are scoped to the picked line, so a selection made on another line cannot
            // survive the switch; going back to every line keeps it, it stays valid mill-wide, as the shift does.
            ...(allProductionLines ? {} : { machineCenter: initialState.filters.machineCenter }),
            productionLine: productionLineId,
        }));
    };

    const handleShowAllProductionLinesChange = (value: boolean) => {
        setShowAllProductionLines(value);
    };

    const handleShowFiltersChange = (value: boolean) => {
        setShowFilters(value);
    };

    const handleDatePresetChange = (preset: OeeDatePresetEnum) => {
        setDatePreset(preset);
    };

    const handleSetFiltersDefault = async (defaultFilters: IOeeSummaryDashboardFilters) => {
        try {
            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.MILL_DASHBOARD_OEE_SUMMARY,
                        filter_selection: JSON.stringify(
                            filterObjectProperties(defaultFilters, validOeeSummaryDefaultFields),
                        ),
                    },
                },
            });
            await refetch();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.MILL_DASHBOARD_OEE_SUMMARY,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const providerValue = {
        datePreset,
        dateRange,
        filters,
        handleDatePresetChange,
        handleFiltersChange,
        handleRemoveDefaultFilters,
        handleSelectedProductionLineChange,
        handleSetFiltersDefault,
        handleShowAllProductionLinesChange,
        handleShowFiltersChange,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
        selectedProductionLineName,
        showAllProductionLines,
        showFilters,
        userFiltersDefault,
    };

    return (
        <OeeSummaryDashboardContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </OeeSummaryDashboardContext.Provider>
    );
};
